/**
 * Core progress storage, default data, migrations, date utilities, and streak helpers.
 */

import type {
  ProgressData,
  ProgressStats,
} from '../../types/progress';
import { CURRENT_PROGRESS_VERSION } from '../../types/progress';
import { getValidatedItem, setStorageItem, removeStorageItem, STORAGE_KEYS } from '../storageService';
import { ProgressDataSchema } from '../schemas';
import { migrateStrengthToSM2, estimateEncountersFromLegacy } from '../migrateMastery';
import { migrateWordMasteryToFIRe } from '../migrateSM2ToFIRe';
import { getDifficultyLevel } from '../progressiveDifficulty';

// ---------------------------------------------------------------------------
// Storage key (uses centralized storageService key)
// ---------------------------------------------------------------------------
const STORAGE_KEY = STORAGE_KEYS.PROGRESS;

// ---------------------------------------------------------------------------
// Default data
// ---------------------------------------------------------------------------

/**
 * Get the default empty progress data
 */
export function getDefaultProgressData(): ProgressData {
  return {
    version: CURRENT_PROGRESS_VERSION,
    exerciseResults: {},
    wordMastery: {},
    lessonProgress: {},
    stats: {
      totalExercisesAttempted: 0,
      totalCorrect: 0,
      currentAnswerStreak: 0,
      bestAnswerStreak: 0,
      currentPracticeStreak: 0,
      bestPracticeStreak: 0,
      lastPracticeDate: null,
    },
  };
}

// ---------------------------------------------------------------------------
// Migrations
// ---------------------------------------------------------------------------

/**
 * Migrate v1 progress data to v2 (adds SM-2 and encounter tracking)
 */
function migrateV1ToV2(data: ProgressData): ProgressData {
  const migrated: ProgressData = {
    ...data,
    version: 2,
    wordMastery: {},
  };

  // Migrate each word mastery entry
  for (const [wordId, mastery] of Object.entries(data.wordMastery)) {
    const sm2 = migrateStrengthToSM2(mastery.strength, mastery.lastPracticed);
    const encounters = estimateEncountersFromLegacy(mastery);

    migrated.wordMastery[wordId] = {
      ...mastery,
      sm2,
      encounters,
    };
  }

  return migrated;
}

/**
 * Migrate v2 progress data to v3 (adds FIRe spaced repetition)
 */
function migrateV2ToV3(data: ProgressData): ProgressData {
  const migrated: ProgressData = {
    ...data,
    version: 3,
    wordMastery: {},
  };

  // Migrate each word mastery entry
  for (const [wordId, mastery] of Object.entries(data.wordMastery)) {
    const fire = migrateWordMasteryToFIRe(mastery);

    migrated.wordMastery[wordId] = {
      ...mastery,
      fire,
    };
  }

  return migrated;
}

/**
 * Migrate v3 progress data to v4 (adds directional strength for progressive difficulty)
 *
 * Estimates recognition and production strength from legacy strength:
 * - Recognition is typically easier, so we estimate it slightly higher
 * - Production is harder, so we estimate it at or below the legacy strength
 */
function migrateV3ToV4(data: ProgressData): ProgressData {
  const migrated: ProgressData = {
    ...data,
    version: 4,
    wordMastery: {},
  };

  // Migrate each word mastery entry
  for (const [wordId, mastery] of Object.entries(data.wordMastery)) {
    // Estimate directional strength from legacy strength
    // Recognition is typically 10-20% higher than production
    const legacyStrength = mastery.strength;

    // Estimate recognition strength (easier, so likely higher)
    const estimatedRecognition = Math.min(100, Math.round(legacyStrength * 1.15));

    // Production strength stays closer to or below legacy
    const estimatedProduction = legacyStrength;

    // Determine levels based on estimated strengths
    const recognitionLevel = getDifficultyLevel(estimatedRecognition);
    const productionLevel = getDifficultyLevel(estimatedProduction);

    migrated.wordMastery[wordId] = {
      ...mastery,
      directionalStrength: {
        recognitionStrength: estimatedRecognition,
        productionStrength: estimatedProduction,
        recognitionLevel,
        productionLevel,
        // Use lastPracticed for both since we don't have separate history
        lastRecognitionPractice: mastery.lastPracticed || null,
        lastProductionPractice: mastery.lastPracticed || null,
      },
    };
  }

  return migrated;
}

// ---------------------------------------------------------------------------
// Date utilities
// ---------------------------------------------------------------------------

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
export function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get yesterday's date in YYYY-MM-DD format (local timezone)
 */
export function getYesterdayDateString(): string {
  const now = new Date();
  now.setDate(now.getDate() - 1);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate days between two date strings
 */
export function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

// ---------------------------------------------------------------------------
// Streak helpers
// ---------------------------------------------------------------------------

/**
 * Update practice streak based on current date
 */
export function updatePracticeStreak(stats: ProgressStats): void {
  const today = getLocalDateString();
  const lastDate = stats.lastPracticeDate;

  if (lastDate === today) {
    // Already practiced today, no change
    return;
  }

  const yesterday = getYesterdayDateString();

  if (lastDate === yesterday) {
    // Consecutive day - increment streak
    stats.currentPracticeStreak += 1;
  } else {
    // Streak broken or first time - reset to 1
    stats.currentPracticeStreak = 1;
  }

  stats.bestPracticeStreak = Math.max(stats.bestPracticeStreak, stats.currentPracticeStreak);
  stats.lastPracticeDate = today;
}

/**
 * Update answer streak based on correctness
 */
export function updateAnswerStreak(stats: ProgressStats, isCorrect: boolean): void {
  if (isCorrect) {
    stats.currentAnswerStreak += 1;
    stats.bestAnswerStreak = Math.max(stats.bestAnswerStreak, stats.currentAnswerStreak);
  } else {
    stats.currentAnswerStreak = 0;
  }
}

// ---------------------------------------------------------------------------
// Core storage operations
// ---------------------------------------------------------------------------

/**
 * Read progress data from localStorage
 */
export function getProgress(): ProgressData {
  try {
    const raw = getValidatedItem(STORAGE_KEY, ProgressDataSchema, null);
    if (!raw) {
      return getDefaultProgressData();
    }
    let current = raw as ProgressData;

    // Handle migrations based on version
    if (current.version === 1) {
      current = migrateV1ToV2(current);
      saveProgress(current); // Persist migration
    }
    if (current.version === 2) {
      current = migrateV2ToV3(current);
      saveProgress(current); // Persist migration
    }
    if (current.version === 3) {
      current = migrateV3ToV4(current);
      saveProgress(current); // Persist migration
    }

    return current;
  } catch {
    if (import.meta.env.DEV) console.error('Failed to read progress from localStorage');
    return getDefaultProgressData();
  }
}

/**
 * Save progress data to localStorage
 */
export function saveProgress(data: ProgressData): void {
  try {
    setStorageItem(STORAGE_KEY, data);
  } catch {
    if (import.meta.env.DEV) console.error('Failed to save progress to localStorage');
  }
}

/**
 * Reset all progress data
 */
export function resetProgress(): void {
  try {
    removeStorageItem(STORAGE_KEY);
  } catch {
    if (import.meta.env.DEV) console.error('Failed to reset progress');
  }
}
